/**
 * Akten — Shared utility functions for Akte (case file) creation
 *
 * Pure functions for generating unique Akten-IDs and building insert payloads.
 * Extracted from AktenPage.tsx for testability and reuse.
 */

import { getAktenNummerPrefix } from './mietwagen';
export { generiereAktenNr } from '@shared/utils/aktenzeichen';

/**
 * Generates a human-readable akten_nummer from the betrieb and akten_nr.
 * Uses the akten_nr suffix to keep them correlated.
 */
export function generiereAktenNummer(betriebId: string, aktenNr: string): string {
  const prefix = getAktenNummerPrefix(betriebId);
  // Extract the suffix after the last dash
  const parts = aktenNr.split('-');
  const suffix = parts.length >= 3 ? parts.slice(1).join('-') : aktenNr.replace(/^[A-Z]{2}/, '');
  return `${prefix}-${suffix}`;
}

/**
 * Builds the insert payload for a Gutachten/standard Akte.
 */
export function erstelleGutachtenInsertData(params: {
  aktenNr: string;
  aktenNummer: string;
  betriebId: string;
  auftragstyp: string;
  auftragsTypLabels: Record<string, string>;
  kennzeichen: string;
  auftraggeber: string;
  istProblemfall: boolean;
  schadennummer?: string;
  versicherung?: string;
  schadendatum?: string;
  quelle?: string;
  anwaltName?: string;
  problemBeschreibung?: string;
}): Record<string, unknown> {
  const {
    aktenNr, aktenNummer, betriebId, auftragstyp, auftragsTypLabels,
    kennzeichen, auftraggeber, istProblemfall,
    schadennummer, versicherung, schadendatum, quelle,
    anwaltName, problemBeschreibung,
  } = params;

  const insertData: Record<string, unknown> = {
    akten_nr: aktenNr,
    akten_nummer: aktenNummer,
    business_unit: betriebId,
    titel: `${auftragsTypLabels[auftragstyp] || auftragstyp} — ${kennzeichen}`,
    status: istProblemfall ? 'in_bearbeitung' : 'neu',
    prioritaet: istProblemfall ? 'hoch' : 'mittel',
    kunde_name: auftraggeber,
    kennzeichen: kennzeichen.toUpperCase(),
    auftragstyp_sv: auftragstyp,
    status_sv: istProblemfall ? 'kuerzung_pruefen' : 'neu',
    created_via: 'manuell',
  };

  if (schadennummer?.trim()) insertData.schadennummer = schadennummer.trim();
  if (versicherung?.trim()) insertData.versicherung = versicherung.trim();
  if (schadendatum) insertData.schadendatum = schadendatum;
  if (schadendatum) insertData.schadentag = schadendatum;
  if (quelle?.trim()) insertData.erteilt_durch = quelle.trim();

  if (istProblemfall || anwaltName?.trim() || problemBeschreibung?.trim()) {
    const notizParts: string[] = [];
    if (istProblemfall) notizParts.push('⚠️ PROBLEMFALL');
    if (anwaltName?.trim()) notizParts.push(`Anwalt: ${anwaltName.trim()}`);
    if (problemBeschreibung?.trim()) notizParts.push(problemBeschreibung.trim());
    insertData.notizen = notizParts.join('\n');
  }

  return insertData;
}

/**
 * Builds Aufgaben insert payloads for a Problemfall Akte.
 * Uses akte_id (UUID) — NOT akten_nr — as the foreign key.
 */
export function erstelleProblemfallAufgaben(params: {
  akteId: string;
  aktenNr: string;
  betriebId: string;
  kennzeichen: string;
  anwaltName?: string;
  versicherung?: string;
  schadennummer?: string;
  problemBeschreibung?: string;
}): Record<string, unknown>[] {
  const {
    akteId, aktenNr, betriebId, kennzeichen,
    anwaltName, versicherung, schadennummer, problemBeschreibung,
  } = params;

  const faellig = new Date();
  faellig.setDate(faellig.getDate() + 2);
  const faelligStr = faellig.toISOString().split('T')[0];

  const aufgabeAnwalt: Record<string, unknown> = {
    titel: `Anwalt anschreiben — ${kennzeichen.toUpperCase()}${anwaltName ? ` (${anwaltName})` : ''}`,
    beschreibung: `Abrechnungsschreiben anfordern für Akte ${aktenNr}.\nVersicherung: ${versicherung || 'N/A'}\nSchadennr: ${schadennummer || 'N/A'}\n${problemBeschreibung ? `Problem: ${problemBeschreibung}` : ''}`,
    status: 'offen',
    prioritaet: 'hoch',
    business_unit: betriebId,
    kategorie: 'anwalt',
    akte_id: akteId,
    faellig_am: faelligStr,
  };

  const aufgabePruefung: Record<string, unknown> = {
    titel: `Abrechnungsschreiben prüfen — ${kennzeichen.toUpperCase()}`,
    beschreibung: `Wenn das Abrechnungsschreiben der ${versicherung || 'Versicherung'} eingeht:\n- Kürzungen identifizieren\n- Differenzen berechnen\n- Ggf. Widerspruch vorbereiten`,
    status: 'offen',
    prioritaet: 'mittel',
    business_unit: betriebId,
    kategorie: 'pruefung',
    akte_id: akteId,
  };

  return [aufgabeAnwalt, aufgabePruefung];
}

/**
 * Checks if a Supabase error is a unique constraint violation.
 */
export function isUniqueViolation(error: { code?: string; message?: string }): boolean {
  return error.code === '23505' || (error.message?.includes('duplicate key') ?? false);
}
